import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { functions, db } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { ArrowLeft, Loader, Calendar, Clock, Copy, Download, Upload, FileSpreadsheet } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const ManagePrayerTimes = () => {
  const { mosqueId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mosque, setMosque] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [prayerTimes, setPrayerTimes] = useState({});
  const [iqamahOffsets, setIqamahOffsets] = useState({});
  const [previousMonths, setPreviousMonths] = useState([]);
  const [error, setError] = useState('');
  const [uploadedData, setUploadedData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isManualEditing, setIsManualEditing] = useState(false);
  const fileInputRef = useRef(null);

  const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const manualEntryFields = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

  const csvEscape = (value) => {
    const stringValue = value == null ? '' : String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const buildCsv = (rows) => rows.map(row => row.map(csvEscape).join(',')).join('\n');
  const normalizeUploadedDate = (rawValue) => {
    if (rawValue == null) return '';
    if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
      const parsed = XLSX.SSF?.parse_date_code?.(rawValue);
      if (parsed?.y && parsed?.m && parsed?.d) {
        const year = String(parsed.y).padStart(4, '0');
        const month = String(parsed.m).padStart(2, '0');
        const day = String(parsed.d).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
    return String(rawValue).trim();
  };
  const clampOffset = (value, min = -120, max = 180) => Math.min(max, Math.max(min, value));
  const parseOffsetOrDefault = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : clampOffset(parsed);
  };
  const findInvalidPrayerTimes = (timesByDate) => {
    const invalidRows = [];
    Object.entries(timesByDate || {}).forEach(([date, times]) => {
      Object.entries(times || {}).forEach(([prayer, value]) => {
        const normalized = (value || '').trim();
        if (normalized && !TIME_24H_REGEX.test(normalized)) {
          invalidRows.push(`${date} [${prayer}=${normalized}]`);
        }
      });
    });
    return invalidRows;
  };

  const sanitizeFileNamePart = (value) => {
    const cleaned = String(value || 'mosque')
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return cleaned || 'mosque';
  };
  const getCurrentLocalMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    const currentMonth = getCurrentLocalMonth();
    setSelectedMonth(currentMonth);
    loadMosque();
  }, [mosqueId]);

  useEffect(() => {
    if (selectedMonth) {
      loadPrayerTimes();
    }
  }, [selectedMonth, mosque?.prayerTimeSource, mosque?.aladhanSettings?.latitude, mosque?.aladhanSettings?.longitude]);

  useEffect(() => {
    // Reset manual entry mode when the selected month changes
    setIsManualEditing(false);
  }, [selectedMonth]);

  const loadMosque = async () => {
    try {
      const mosqueDoc = await getDoc(doc(db, 'mosques', mosqueId));
      if (mosqueDoc.exists()) {
        setMosque(mosqueDoc.data());
        await loadPreviousMonths();
      } else {
        setError('Mosque not found');
      }
    } catch (err) {
      console.error('Error loading mosque:', err);
      setError('Failed to load mosque');
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousMonths = async () => {
    try {
      const prayerTimesRef = collection(db, 'mosques', mosqueId, 'prayerTimes');
      const snapshot = await getDocs(prayerTimesRef);
      const months = snapshot.docs.map(doc => doc.id).sort().reverse();
      setPreviousMonths(months);
    } catch (err) {
      console.error('Error loading previous months:', err);
    }
  };

  const loadPrayerTimes = async () => {
    try {
      const prayerTimeDoc = await getDoc(
        doc(db, 'mosques', mosqueId, 'prayerTimes', selectedMonth)
      );

      if (prayerTimeDoc.exists()) {
        const data = prayerTimeDoc.data();
        setPrayerTimes(data.prayerTimes || {});
        setIqamahOffsets(data.iqamahOffsets || {});
      } else {
        // Generate from Aladhan if configured
        if (mosque?.prayerTimeSource === 'aladhan') {
          await generateFromAladhan();
        } else if (!mosque) {
          // Mosque metadata is still loading; wait for next effect trigger
          return;
        } else {
          setPrayerTimes({});
          setIqamahOffsets({});
        }
      }
      setIsManualEditing(false);
    } catch (err) {
      console.error('Error loading prayer times:', err);
      setError('Failed to load prayer times');
    }
  };

  const buildMonthPrayerTemplate = (existingTimes = {}) => {
    if (!selectedMonth) return existingTimes;

    const [yearStr, monthStr] = selectedMonth.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    if (Number.isNaN(year) || Number.isNaN(month)) {
      return existingTimes;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    const template = {};

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = `${selectedMonth}-${String(day).padStart(2, '0')}`;
      const existing = existingTimes[date] || {};
      template[date] = {
        fajr: existing.fajr || '',
        sunrise: existing.sunrise || '',
        dhuhr: existing.dhuhr || '',
        asr: existing.asr || '',
        maghrib: existing.maghrib || '',
        isha: existing.isha || ''
      };
    }

    return template;
  };

  const enableManualEntry = () => {
    setPrayerTimes(prev => buildMonthPrayerTemplate(prev));
    setIsManualEditing(true);
  };

  const generateFromAladhan = async () => {
    if (!mosque?.aladhanSettings) return;

    try {
      const [year, month] = selectedMonth.split('-');
      const settings = mosque.aladhanSettings;

      const response = await fetch(
        `https://api.aladhan.com/v1/calendar/${year}/${month}?` +
        `latitude=${settings.latitude}&longitude=${settings.longitude}&` +
        `method=${settings.calculationMethod}&school=${settings.school}`
      );
      if (!response.ok) {
        throw new Error(`Aladhan request failed (${response.status})`);
      }

      const data = await response.json();

      if (data.code === 200) {
        const times = {};
        data.data.forEach(day => {
          const date = day.date.gregorian.date;
          times[date] = {
            fajr: day.timings.Fajr.split(' ')[0],
            sunrise: day.timings.Sunrise.split(' ')[0],
            dhuhr: day.timings.Dhuhr.split(' ')[0],
            asr: day.timings.Asr.split(' ')[0],
            maghrib: day.timings.Maghrib.split(' ')[0],
            isha: day.timings.Isha.split(' ')[0]
          };
        });
        setPrayerTimes(times);

        // Set default iqamah offsets (15 minutes after adhan)
        const defaultOffsets = {};
        prayers.forEach(prayer => {
          defaultOffsets[prayer] = 15;
        });
        setIqamahOffsets(defaultOffsets);
      } else {
        throw new Error(data?.data || 'Unexpected Aladhan response');
      }
    } catch (err) {
      console.error('Error generating from Aladhan:', err);
      setError('Failed to generate prayer times from Aladhan');
    }
  };

  const copyFromPreviousMonth = async (monthToCopy) => {
    try {
      const prayerTimeDoc = await getDoc(
        doc(db, 'mosques', mosqueId, 'prayerTimes', monthToCopy)
      );

      if (prayerTimeDoc.exists()) {
        const data = prayerTimeDoc.data();
        // Copy only iqamah offsets, not the prayer times themselves
        setIqamahOffsets(data.iqamahOffsets || {});
        setError('');
      } else {
        setError(`No prayer times found for ${monthToCopy}`);
      }
    } catch (err) {
      console.error('Error copying from previous month:', err);
      setError('Failed to copy from previous month');
    }
  };

  const handleIqamahOffsetChange = (prayer, value) => {
    const parsed = parseInt(value, 10);
    const normalized = Number.isNaN(parsed) ? 0 : clampOffset(parsed);
    setIqamahOffsets(prev => ({
      ...prev,
      [prayer]: normalized
    }));
  };

  const handlePrayerTimeChange = (date, prayer, value) => {
    setPrayerTimes(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [prayer]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const invalidRows = findInvalidPrayerTimes(prayerTimes);
      if (invalidRows.length > 0) {
        const preview = invalidRows.slice(0, 5).join('; ');
        setError(`Invalid prayer time format. Use HH:mm (24h). Examples: ${preview}${invalidRows.length > 5 ? '; ...' : ''}`);
        return;
      }

      const setPrayerTimesFunc = httpsCallable(functions, 'setPrayerTimes');
      const normalizedOffsets = {
        fajr: parseOffsetOrDefault(iqamahOffsets?.fajr, 15),
        dhuhr: parseOffsetOrDefault(iqamahOffsets?.dhuhr, 15),
        asr: parseOffsetOrDefault(iqamahOffsets?.asr, 15),
        maghrib: parseOffsetOrDefault(iqamahOffsets?.maghrib, 5),
        isha: parseOffsetOrDefault(iqamahOffsets?.isha, 15)
      };
      await setPrayerTimesFunc({
        mosqueId,
        month: selectedMonth,
        prayerTimes,
        iqamahOffsets: normalizedOffsets
      });

      navigate('/admin/mosques');
    } catch (err) {
      console.error('Error saving prayer times:', err);
      setError(err.message || 'Failed to save prayer times');
    } finally {
      setSaving(false);
    }
  };

  const exportToCSV = () => {
    const rows = [
      ['Date', 'Fajr', 'Fajr Iqamah', 'Sunrise', 'Dhuhr', 'Dhuhr Iqamah', 'Asr', 'Asr Iqamah', 'Maghrib', 'Maghrib Iqamah', 'Isha', 'Isha Iqamah']
    ];

    sortedPrayerTimes.forEach(([date, times]) => {
      const row = [date];
      const fajrTime = times.fajr || '';
      const fajrOffset = iqamahOffsets.fajr;
      const fajrIqamah = fajrTime && fajrOffset !== undefined
        ? calculateIqamah(fajrTime, fajrOffset)
        : '';
      row.push(fajrTime, fajrIqamah);

      row.push(times.sunrise || '');

      ['dhuhr', 'asr', 'maghrib', 'isha'].forEach(prayer => {
        const adhanTime = times[prayer] || '';
        const offset = iqamahOffsets[prayer];
        const iqamahTime = adhanTime && offset !== undefined
          ? calculateIqamah(adhanTime, offset)
          : '';
        row.push(adhanTime, iqamahTime);
      });
      rows.push(row);
    });

    const csv = buildCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prayer-times-${sanitizeFileNamePart(mosque?.name)}-${selectedMonth}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const calculateIqamah = (adhanTime, offset) => {
    const [hours, minutes] = adhanTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + offset;
    const normalizedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
    const iqamahHours = Math.floor(normalizedMinutes / 60);
    const iqamahMinutes = normalizedMinutes % 60;
    return `${String(iqamahHours).padStart(2, '0')}:${String(iqamahMinutes).padStart(2, '0')}`;
  };

  const downloadTemplate = () => {
    const currentYear = new Date().getFullYear();
    const isLeapYear = new Date(currentYear, 1, 29).getMonth() === 1;
    const totalDaysInYear = isLeapYear ? 366 : 365;
    const rows = [
      ['Date', 'Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha', 'Fajr Iqamah Offset (min)', 'Dhuhr Iqamah Offset (min)', 'Asr Iqamah Offset (min)', 'Maghrib Iqamah Offset (min)', 'Isha Iqamah Offset (min)']
    ];

    // Generate example row for first day of current year
    const exampleDate = `${currentYear}-01-01`;
    rows.push([exampleDate, '06:30', '08:15', '12:45', '14:30', '16:45', '18:30', '15', '15', '15', '5', '15']);

    // Add empty rows for rest of year
    for (let i = 1; i < totalDaysInYear; i++) {
      const date = new Date(currentYear, 0, i + 1);
      const dateStr = date.toISOString().split('T')[0];
      rows.push([dateStr, '', '', '', '', '', '', '15', '15', '15', '5', '15']);
    }

    const csv = buildCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prayer-times-template-${currentYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedData(null);
    setShowPreview(false);
    setError('');

    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      // Parse CSV
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          processUploadedData(results.data);
        },
        error: (error) => {
          setError(`Failed to parse CSV: ${error.message}`);
        }
      });
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      // Parse Excel
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          processUploadedData(jsonData);
        } catch (error) {
          setError(`Failed to parse Excel: ${error.message}`);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setError('Please upload a CSV or Excel file');
    }

    // Reset file input
    e.target.value = '';
  };

  const processUploadedData = (data) => {
    try {
      const monthsData = {}; // Group by month
      const invalidTimeRows = [];

      data.forEach(row => {
        // Skip empty rows
        const date = normalizeUploadedDate(row.Date);
        if (!date) return;

        // Validate date format (YYYY-MM-DD)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          console.warn('Skipping invalid date format:', date);
          return;
        }
        const [y, m, d] = date.split('-').map(Number);
        const parsedDate = new Date(y, m - 1, d);
        if (Number.isNaN(parsedDate.getTime()) || parsedDate.getFullYear() !== y || parsedDate.getMonth() !== m - 1 || parsedDate.getDate() !== d) {
          console.warn('Skipping invalid calendar date:', date);
          return;
        }

        const month = date.substring(0, 7); // YYYY-MM

        if (!monthsData[month]) {
          monthsData[month] = { times: {}, offsets: {} };
        }

        const parsedTimes = {
          fajr: row.Fajr?.trim() || '',
          sunrise: row.Sunrise?.trim() || '',
          dhuhr: row.Dhuhr?.trim() || '',
          asr: row.Asr?.trim() || '',
          maghrib: row.Maghrib?.trim() || '',
          isha: row.Isha?.trim() || ''
        };

        const invalidTimesForRow = Object.entries(parsedTimes)
          .filter(([, value]) => value && !TIME_24H_REGEX.test(value))
          .map(([prayer, value]) => `${prayer}=${value}`);

        if (invalidTimesForRow.length > 0) {
          invalidTimeRows.push(`${date} [${invalidTimesForRow.join(', ')}]`);
          return;
        }

        // Extract prayer times
        monthsData[month].times[date] = parsedTimes;

        // Extract iqamah offsets (use first occurrence for each month)
        if (monthsData[month].offsets.fajr === undefined) {
          monthsData[month].offsets = {
            fajr: parseOffsetOrDefault(row['Fajr Iqamah Offset (min)'], 15),
            dhuhr: parseOffsetOrDefault(row['Dhuhr Iqamah Offset (min)'], 15),
            asr: parseOffsetOrDefault(row['Asr Iqamah Offset (min)'], 15),
            maghrib: parseOffsetOrDefault(row['Maghrib Iqamah Offset (min)'], 5),
            isha: parseOffsetOrDefault(row['Isha Iqamah Offset (min)'], 15)
          };
        }
      });

      if (Object.keys(monthsData).length === 0) {
        setError('No valid prayer time data found in file');
        return;
      }

      if (invalidTimeRows.length > 0) {
        const preview = invalidTimeRows.slice(0, 5).join('; ');
        setError(`Invalid time format detected. Use HH:mm (24h). Examples: ${preview}${invalidTimeRows.length > 5 ? '; ...' : ''}`);
        return;
      }

      setUploadedData(monthsData);
      setShowPreview(true);
      setError('');
    } catch (err) {
      console.error('Error processing uploaded data:', err);
      setError(`Failed to process file: ${err.message}`);
    }
  };

  const applyUploadedData = () => {
    if (!uploadedData || !selectedMonth) return;

    const monthData = uploadedData[selectedMonth];
    if (monthData) {
      setPrayerTimes(monthData.times);
      setIqamahOffsets(monthData.offsets);
      setShowPreview(false);
      setUploadedData(null);
    } else {
      setError(`No data found for ${selectedMonth} in uploaded file`);
    }
  };

  const saveAllMonths = async () => {
    if (!uploadedData) return;

    setSaving(true);
    setError('');

    try {
      const setPrayerTimesFunc = httpsCallable(functions, 'setPrayerTimes');
      const months = Object.keys(uploadedData).sort();

      for (const month of months) {
        const monthData = uploadedData[month];
        const invalidRows = findInvalidPrayerTimes(monthData?.times);
        if (invalidRows.length > 0) {
          const preview = invalidRows.slice(0, 5).join('; ');
          setError(`Invalid prayer time format in ${month}. Use HH:mm (24h). Examples: ${preview}${invalidRows.length > 5 ? '; ...' : ''}`);
          setSaving(false);
          return;
        }
      }

      for (const month of months) {
        const monthData = uploadedData[month];
        const normalizedOffsets = {
          fajr: parseOffsetOrDefault(monthData?.offsets?.fajr, 15),
          dhuhr: parseOffsetOrDefault(monthData?.offsets?.dhuhr, 15),
          asr: parseOffsetOrDefault(monthData?.offsets?.asr, 15),
          maghrib: parseOffsetOrDefault(monthData?.offsets?.maghrib, 5),
          isha: parseOffsetOrDefault(monthData?.offsets?.isha, 15)
        };
        await setPrayerTimesFunc({
          mosqueId,
          month,
          prayerTimes: monthData.times,
          iqamahOffsets: normalizedOffsets
        });
      }

      setShowPreview(false);
      setUploadedData(null);
      navigate('/admin/mosques');
    } catch (err) {
      console.error('Error saving prayer times:', err);
      setError(err.message || 'Failed to save prayer times');
    } finally {
      setSaving(false);
    }
  };

  const tableFields = isManualEditing ? manualEntryFields : prayers;
  const sortedPrayerTimes = Object.entries(prayerTimes || {}).sort(([dateA], [dateB]) =>
    dateA.localeCompare(dateB)
  );
  const visiblePrayerTimes = isManualEditing ? sortedPrayerTimes : sortedPrayerTimes.slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/mosques')}
          className={`flex items-center gap-2 mb-4 text-sm transition-colors ${
            isDarkMode
              ? 'text-gray-400 hover:text-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Mosques
        </button>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Manage Prayer Times
            </h1>
            <p className={`mt-1 text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {mosque?.name}
            </p>
          </div>

          <div className="flex gap-3 flex-wrap">
            <button
              onClick={downloadTemplate}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                isDarkMode
                  ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Download Template
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                isDarkMode
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload CSV/Excel
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />

            <button
              onClick={isManualEditing ? () => setIsManualEditing(false) : enableManualEntry}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                isDarkMode
                  ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
            >
              {isManualEditing ? 'Finish Manual Entry' : 'Manual Entry'}
            </button>

            {mosque?.prayerTimeSource === 'aladhan' && (
              <button
                onClick={generateFromAladhan}
                className={`px-4 py-2 rounded-xl transition-colors ${
                  isDarkMode
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                Generate from Aladhan
              </button>
            )}

            {Object.keys(prayerTimes).length > 0 && (
              <button
                onClick={exportToCSV}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                    : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                }`}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mb-6 p-4 rounded-xl border ${
          isDarkMode
            ? 'bg-red-900/20 border-red-500/30 text-red-400'
            : 'bg-red-50 border-red-200 text-red-600'
        }`}>
          {error}
        </div>
      )}

      {/* Month Selector */}
      <div className={`mb-6 p-6 rounded-[24px] border ${
        isDarkMode
          ? 'bg-[#111111] border-gray-800'
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className={`block text-sm font-medium mb-2 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Select Month
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-xl border ${
                isDarkMode
                  ? 'bg-[#0A0A0A] border-gray-700 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>

          {previousMonths.length > 0 && (
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Copy Iqamah from
              </label>
              <div className="flex gap-2">
                <select
                  onChange={(e) => e.target.value && copyFromPreviousMonth(e.target.value)}
                  className={`flex-1 px-4 py-2.5 rounded-xl border ${
                    isDarkMode
                      ? 'bg-[#0A0A0A] border-gray-700 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Select month...</option>
                  {previousMonths.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Iqamah Offsets */}
      <div className={`mb-6 p-6 rounded-[24px] border ${
        isDarkMode
          ? 'bg-[#111111] border-gray-800'
          : 'bg-white border-gray-200'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          <Clock className="w-5 h-5" />
          Iqamah Offset (minutes after Adhan)
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {prayers.map(prayer => (
            <div key={prayer}>
              <label className={`block text-sm font-medium mb-2 capitalize ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {prayer}
              </label>
              <input
                type="number"
                value={iqamahOffsets[prayer] || 0}
                onChange={(e) => handleIqamahOffsetChange(prayer, e.target.value)}
                className={`w-full px-4 py-2.5 rounded-xl border ${
                  isDarkMode
                    ? 'bg-[#0A0A0A] border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                placeholder="0"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Prayer Times Table */}
      {sortedPrayerTimes.length > 0 ? (
        <div className={`mb-6 rounded-[24px] border overflow-hidden ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-medium ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Date
                  </th>
                  {tableFields.map(prayer => (
                    <th key={prayer} className={`px-4 py-3 text-left text-sm font-medium capitalize ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {prayer.replace('_', ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePrayerTimes.map(([date, times]) => (
                  <tr key={date} className={`border-t ${
                    isDarkMode ? 'border-gray-800' : 'border-gray-200'
                  }`}>
                    <td className={`px-4 py-3 text-sm ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {date}
                    </td>
                    {tableFields.map(prayer => (
                      <td key={prayer} className="px-4 py-3">
                        {isManualEditing ? (
                          <div className="space-y-1">
                            <input
                              type="time"
                              value={times[prayer] || ''}
                              onChange={(e) => handlePrayerTimeChange(date, prayer, e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg border text-sm ${
                                isDarkMode
                                  ? 'bg-[#0A0A0A] border-gray-700 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              step="60"
                            />
                            {prayers.includes(prayer) && times[prayer] && iqamahOffsets[prayer] !== undefined && (
                              <div className={`text-xs ${
                                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                              }`}>
                                Iqamah: {calculateIqamah(times[prayer], iqamahOffsets[prayer])}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {times[prayer] || '-'}
                            {prayers.includes(prayer) && times[prayer] && iqamahOffsets[prayer] !== undefined && (
                              <div className={`text-xs mt-1 ${
                                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                              }`}>
                                Iqamah: {calculateIqamah(times[prayer], iqamahOffsets[prayer])}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isManualEditing && sortedPrayerTimes.length > 10 && (
            <div className={`px-4 py-3 text-center text-sm ${
              isDarkMode ? 'text-gray-400 bg-[#0A0A0A]' : 'text-gray-600 bg-gray-50'
            }`}>
              Showing first 10 days. Full month will be saved.
            </div>
          )}
        </div>
      ) : (
        <div className={`mb-6 p-12 rounded-[24px] border text-center ${
          isDarkMode
            ? 'bg-[#111111] border-gray-800'
            : 'bg-white border-gray-200'
        }`}>
          <Calendar className={`w-12 h-12 mx-auto mb-4 ${
            isDarkMode ? 'text-gray-600' : 'text-gray-400'
          }`} />
          <p className={`text-sm ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            No prayer times for this month yet.
            {mosque?.prayerTimeSource === 'aladhan'
              ? ' Click "Generate from Aladhan" to auto-generate.'
              : ' Click "Manual Entry" to input times yourself.'}
          </p>
        </div>
      )}

      {/* Save Button */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate('/admin/mosques')}
          className={`flex-1 px-6 py-3 rounded-xl transition-colors ${
            isDarkMode
              ? 'bg-gray-800 text-white hover:bg-gray-700'
              : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
          }`}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || Object.keys(prayerTimes).length === 0}
          className={`flex-1 px-6 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 ${
            isDarkMode
              ? 'bg-emerald-500 text-black hover:bg-emerald-400 disabled:bg-emerald-500/50'
              : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
          }`}
        >
          {saving ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Prayer Times'
          )}
        </button>
      </div>

      {/* Upload Preview Modal */}
      {showPreview && uploadedData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className={`max-w-4xl w-full max-h-[90vh] overflow-auto rounded-[24px] border ${
            isDarkMode
              ? 'bg-[#0A0A0A] border-gray-800'
              : 'bg-white border-gray-200'
          }`}>
            <div className={`sticky top-0 p-6 border-b ${
              isDarkMode
                ? 'bg-[#0A0A0A] border-gray-800'
                : 'bg-white border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Preview Uploaded Prayer Times
              </h2>
              <p className={`mt-1 text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Found {Object.keys(uploadedData).length} month(s) of prayer times
              </p>
            </div>

            <div className="p-6 space-y-4">
              {Object.entries(uploadedData).map(([month, data]) => (
                <div key={month} className={`p-4 rounded-xl border ${
                  isDarkMode
                    ? 'bg-[#111111] border-gray-800'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <h3 className={`font-semibold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {month} ({Object.keys(data.times).length} days)
                  </h3>
                  <p className={`text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Iqamah offsets: Fajr +{data.offsets.fajr}min, Dhuhr +{data.offsets.dhuhr}min,
                    Asr +{data.offsets.asr}min, Maghrib +{data.offsets.maghrib}min, Isha +{data.offsets.isha}min
                  </p>
                </div>
              ))}
            </div>

            <div className={`sticky bottom-0 p-6 border-t flex gap-4 ${
              isDarkMode
                ? 'bg-[#0A0A0A] border-gray-800'
                : 'bg-white border-gray-200'
            }`}>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setUploadedData(null);
                }}
                className={`flex-1 px-6 py-3 rounded-xl transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                    : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={applyUploadedData}
                className={`flex-1 px-6 py-3 rounded-xl transition-colors ${
                  isDarkMode
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                Apply to {selectedMonth} Only
              </button>
              <button
                onClick={saveAllMonths}
                disabled={saving}
                className={`flex-1 px-6 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 ${
                  isDarkMode
                    ? 'bg-emerald-500 text-black hover:bg-emerald-400 disabled:bg-emerald-500/50'
                    : 'bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-400'
                }`}
              >
                {saving ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  `Save All ${Object.keys(uploadedData).length} Month(s)`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePrayerTimes;
